import React, { useState, useEffect } from 'react';
import { 
  FileText, Download, Copy, Check, AlertCircle, ArrowLeft, 
  Sparkles, Upload, X, ShieldCheck, CheckCircle2, 
  Building2, Users, Scale, Calculator, BookOpen, RefreshCw, FileCheck,
  Eye, Code
} from 'lucide-react';
import { DigitalSignatureModal } from './DigitalSignatureModal';
import MarkdownRenderer from './MarkdownRenderer';

interface EnterpriseBlankDocumentAgentViewProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  currentUserEmail?: string;
  currency?: string;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

interface BlankInfo {
  name: string;
  filePath: string;
  uploadedAt: string;
  fileSize?: number;
}

interface BlanksState {
  order?: BlankInfo;
  letter?: BlankInfo;
  directive?: BlankInfo;
}

interface RoleUsed {
  id: string;
  name: string;
  domain: string;
}

interface DraftResult {
  docType: string;
  title: string;
  docNumber: string;
  docDate: string;
  city: string;
  rawText: string;
  docxUrl: string;
  pdfUrl?: string;
  rolesUsed: RoleUsed[];
  complianceAudit: {
    dstuStandard: string;
    allMandatoryRequisitesPresent: boolean;
    languageCorrectnessScore: number;
    notes: string[];
  };
  blankUsed: {
    type: 'custom' | 'auto_dstu';
    name: string;
  };
}

export default function EnterpriseBlankDocumentAgentView({
  language,
  onBack,
  onGoHome,
  currentUserEmail,
  useEnterpriseContext,
  enterpriseName: initialEnterpriseName
}: EnterpriseBlankDocumentAgentViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';

  // Form State
  const [docType, setDocType] = useState<'order' | 'letter' | 'directive'>('order');
  const [taskDescription, setTaskDescription] = useState('');
  const [blankOption, setBlankOption] = useState<'custom' | 'auto_dstu'>('auto_dstu');
  const [includeKnowledgeBase, setIncludeKnowledgeBase] = useState(true);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

  // Enterprise & Blanks State
  const [enterpriseName, setEnterpriseName] = useState(initialEnterpriseName || '');
  const [enterpriseEdrpou, setEnterpriseEdrpou] = useState('');
  const [blanks, setBlanks] = useState<BlanksState>({});
  const [loadingBlanks, setLoadingBlanks] = useState(false);

  // Execution State
  const [isDrafting, setIsDrafting] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DraftResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [showRawMarkdown, setShowRawMarkdown] = useState(false);

  // Digital Signature Modal
  const [showSigModal, setShowSigModal] = useState(false);

  const STEPS = [
    isUk ? "Аналіз фабули завдання та вилучення тексту вхідних файлів..." : "Analyzing task description & extracting input file contents...",
    isUk ? "Підключення експертних промптів (Юрист + Бухгалтер + Кадровик)..." : "Activating expert prompts (Lawyer + Accountant + HR from Vault)...",
    isUk ? "Складання розпорядчої частини за стандартами діловодства..." : "Drafting regulatory preamble & body points...",
    isUk ? "Лінгвістичний аудит за Українським правописом 2019 та ДСТУ 4163:2020..." : "Linguistic auditing (Spelling 2019 & DSTU 4163:2020 compliance)...",
    isUk ? "Рендеринг високої точності на фірмовому бланку у MS Word (.docx) та PDF..." : "High-fidelity rendering onto enterprise letterhead in Word & PDF..."
  ];

  // Fetch enterprise blanks
  useEffect(() => {
    const fetchBlanks = async () => {
      setLoadingBlanks(true);
      try {
        const res = await fetch('/api/enterprise/blanks');
        if (res.ok) {
          const data = await res.json();
          setBlanks(data.blanks || {});
          setEnterpriseName(data.enterpriseName || '');
          setEnterpriseEdrpou(data.edrpou || '');
          // If custom blank exists for default docType, auto-select it
          if (data.blanks && data.blanks.order) {
            setBlankOption('custom');
          }
        }
      } catch (err) {
        console.warn("Could not fetch enterprise blanks:", err);
      } finally {
        setLoadingBlanks(false);
      }
    };
    fetchBlanks();
  }, []);

  // Cycle loading steps during drafting
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isDrafting) {
      setLoadingStep(0);
      interval = setInterval(() => {
        setLoadingStep((prev) => (prev < STEPS.length - 1 ? prev + 1 : prev));
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isDrafting]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    setAttachedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (idx: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleRunDraft = async () => {
    if (!taskDescription.trim()) {
      setError(isUk ? "Будь ласка, введіть завдання (фабулу) для складання документа." : "Please enter the document task description.");
      return;
    }

    setIsDrafting(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("docType", docType);
      formData.append("taskDescription", taskDescription);
      formData.append("blankType", blankOption);
      formData.append("includeKnowledgeBase", String(includeKnowledgeBase));
      formData.append("language", language);

      attachedFiles.forEach(file => {
        formData.append("files", file);
      });

      const res = await fetch("/api/enterprise/draft-blank-document", {
        method: "POST",
        body: formData
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || (isUk ? "Помилка складання документа на бланку." : "Failed to draft document on blank."));
      }

      const data: DraftResult = await res.json();
      setResult(data);
    } catch (err: any) {
      console.error("Drafting error:", err);
      setError(err.message || (isUk ? "Сталася помилка при зверненні до сервісу." : "An error occurred."));
    } finally {
      setIsDrafting(false);
    }
  };

  const hasCustomBlankForSelectedType = !!(blanks && blanks[docType]);

  const applyPreset = (presetText: string, targetType: 'order' | 'letter' | 'directive') => {
    setDocType(targetType);
    setTaskDescription(presetText);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-16">
      {/* Top Header Navigation */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-zinc-900/90 border border-zinc-800 p-4 rounded-2xl shadow-xl">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl transition-colors cursor-pointer"
            title={isUk ? "Назад" : "Back"}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                ДСТУ 4163:2020 • Multi-Agent Suite
              </span>
              {enterpriseName && (
                <span className="text-xs text-zinc-400 font-mono">
                  {enterpriseName} {enterpriseEdrpou ? `(ЄДРПОУ: ${enterpriseEdrpou})` : ''}
                </span>
              )}
            </div>
            <h1 className="text-lg sm:text-xl font-black text-white flex items-center gap-2 mt-0.5">
              <FileText className="w-5 h-5 text-emerald-400" />
              <span>
                {isPl 
                  ? "Kreator Pism i Zarządzeń na Papierze Firmowym" 
                  : isEn 
                  ? "Enterprise Orders, Letters & Directives Generator" 
                  : "Конструктор Наказів та Листів на Фірмовому Бланку"}
              </span>
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {onGoHome && (
            <button
              onClick={onGoHome}
              className="px-3 py-1.5 text-xs text-zinc-400 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors cursor-pointer"
            >
              {isUk ? "Головна" : "Home"}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Form & Inputs */}
        <div className="lg:col-span-5 space-y-5">
          {/* Document Type Selector */}
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 space-y-3 shadow-md">
            <label className="text-xs font-bold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
              <span>1. {isUk ? "Тип документа" : "Document Type"}</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'order', labelUk: 'Наказ', labelEn: 'Order', icon: FileCheck },
                { id: 'letter', labelUk: 'Службовий лист', labelEn: 'Letter', icon: FileText },
                { id: 'directive', labelUk: 'Розпорядження', labelEn: 'Directive', icon: Scale }
              ].map((item) => {
                const IconComp = item.icon;
                const isSelected = docType === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      setDocType(item.id as any);
                      if (blanks && blanks[item.id as keyof BlanksState]) {
                        setBlankOption('custom');
                      }
                    }}
                    className={`p-3 rounded-xl border text-center transition-all cursor-pointer flex flex-col items-center gap-1.5 ${
                      isSelected
                        ? 'bg-emerald-500/20 border-emerald-500 text-emerald-300 shadow-md shadow-emerald-500/10'
                        : 'bg-zinc-950/60 border-zinc-800 hover:border-zinc-700 text-zinc-400'
                    }`}
                  >
                    <IconComp className={`w-5 h-5 ${isSelected ? 'text-emerald-400' : 'text-zinc-500'}`} />
                    <span className="text-xs font-bold">{isUk ? item.labelUk : item.labelEn}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Letterhead Blank Selector */}
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 space-y-3 shadow-md">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
                <span>2. {isUk ? "Фірмовий бланк" : "Letterhead Blank"}</span>
              </label>
              {hasCustomBlankForSelectedType ? (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                  {isUk ? "✓ Завантажено фірмовий бланк" : "✓ Custom blank active"}
                </span>
              ) : (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-blue-500/20 text-blue-400 border border-blue-500/30">
                  {isUk ? "🏛️ ДСТУ 4163:2020 Auto" : "🏛️ Standard DSTU"}
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setBlankOption('custom')}
                disabled={!hasCustomBlankForSelectedType}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer flex flex-col gap-1 ${
                  blankOption === 'custom'
                    ? 'bg-purple-500/20 border-purple-500 text-purple-300'
                    : hasCustomBlankForSelectedType
                    ? 'bg-zinc-950/60 border-zinc-800 hover:border-zinc-700 text-zinc-400'
                    : 'bg-zinc-950/20 border-zinc-900 text-zinc-600 cursor-not-allowed opacity-50'
                }`}
              >
                <div className="flex items-center gap-1.5 text-xs font-bold">
                  <span>📄 {isUk ? "Фірмовий бланк компанії" : "Custom Enterprise Blank"}</span>
                </div>
                <span className="text-[10px] text-zinc-400 line-clamp-1">
                  {hasCustomBlankForSelectedType ? blanks[docType]?.name : (isUk ? "Не завантажено в кабінеті" : "Not uploaded")}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setBlankOption('auto_dstu')}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer flex flex-col gap-1 ${
                  blankOption === 'auto_dstu'
                    ? 'bg-emerald-500/20 border-emerald-500 text-emerald-300'
                    : 'bg-zinc-950/60 border-zinc-800 hover:border-zinc-700 text-zinc-400'
                }`}
              >
                <div className="flex items-center gap-1.5 text-xs font-bold">
                  <span>🏛️ {isUk ? "Еталонний ДСТУ 4163:2020" : "Canonical DSTU Blank"}</span>
                </div>
                <span className="text-[10px] text-zinc-400">
                  {isUk ? "Генерація з Corporate DNA" : "Inherited from Corporate DNA"}
                </span>
              </button>
            </div>
          </div>

          {/* Task Description */}
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 space-y-3 shadow-md">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
                <span>3. {isUk ? "Завдання та зміст наказу / листа" : "Task Details"}</span>
              </label>
              <span className="text-[10px] text-zinc-500 font-mono">
                {taskDescription.length} {isUk ? "симв." : "chars"}
              </span>
            </div>

            <textarea
              rows={5}
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              placeholder={
                docType === 'order'
                  ? (isUk 
                    ? "Опишіть суть наказу: Наприклад: Наказ про преміювання працівників за підсумками 3 кварталу 2026 р. Преміювати начальника відділу логістики (80% окладу), бухгалтера (50% окладу) за перевиконання плану. Джерело виплати - прибуток. Контроль покласти на заступника директора..." 
                    : "Describe the order details: e.g. Quarterly bonuses, appointment, vacation, budget allocation...")
                  : (isUk 
                    ? "Опишіть зміст листа: Наприклад: Відповідь директору ТОВ «Партнер» на лист № 45 щодо строків постачання обладнання. Підтверджуємо готовність відвантажити партію до 25 вересня за умови оплати 50% авансу згідно пункту 4.2 Договору..." 
                    : "Describe the letter content: Response to partner request, official claim, cooperation inquiry...")
              }
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 rounded-xl p-3.5 text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none transition-colors"
            />

            {/* Quick Presets */}
            <div className="space-y-1.5 pt-1">
              <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider">
                {isUk ? "Швидкі сценарії (1-клік):" : "Quick Presets:"}
              </span>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => applyPreset(
                    "Наказ про преміювання працівників за підсумками успішної реалізації проекту за 3 квартал 2026 року. Преміювати провідного інженера у розмірі 70% посадового окладу, бухгалтера у розмірі 50% посадового окладу. Виплату провести у межах фонду оплати праці. Контроль залишаю за собою.",
                    'order'
                  )}
                  className="text-[11px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-2.5 py-1 rounded-lg transition-colors cursor-pointer"
                >
                  🏆 {isUk ? "Преміювання за квартал" : "Quarterly Bonus"}
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset(
                    "Наказ про введення в дію внутрішнього Регламенту документообігу та затвердження обов'язкового використання фірмових бланків за ДСТУ 4163:2020. Керівникам підрозділів забезпечити дотримання регламенту. Юрисконсульту здійснювати правову експертизу.",
                    'order'
                  )}
                  className="text-[11px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-2.5 py-1 rounded-lg transition-colors cursor-pointer"
                >
                  📜 {isUk ? "Введення Регламенту" : "Internal Policy"}
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset(
                    "Офіційний лист-відповідь контрагенту щодо врегулювання порядку оплати та надання первинних документів. Підтверджуємо виконання зобов'язань, наголошуємо на необхідності підписання Актів приймання-передачі з накладенням КЕП до 15 числа.",
                    'letter'
                  )}
                  className="text-[11px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-2.5 py-1 rounded-lg transition-colors cursor-pointer"
                >
                  ✉️ {isUk ? "Лист-відповідь контрагенту" : "Letter to Partner"}
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset(
                    "Наказ про службове відрядження керівника департаменту до філії у м. Львів строком на 5 календарних днів для аудиту операційної діяльності та узгодження квартального плану. Витрати відшкодувати за рахунок коштів підприємства.",
                    'order'
                  )}
                  className="text-[11px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-2.5 py-1 rounded-lg transition-colors cursor-pointer"
                >
                  🚆 {isUk ? "Кадрове відрядження" : "Business Trip"}
                </button>
              </div>
            </div>
          </div>

          {/* Upload Input Information Files */}
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 space-y-3 shadow-md">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
                <span>4. {isUk ? "Вхідні файли та первинні документи" : "Input Reference Files"}</span>
              </label>
              <span className="text-[10px] text-zinc-500 font-mono">
                {attachedFiles.length} {isUk ? "файлів" : "files"}
              </span>
            </div>

            <label className="border-2 border-dashed border-zinc-700/80 hover:border-emerald-500/60 rounded-xl p-4 flex flex-col items-center justify-center gap-1.5 cursor-pointer transition-all bg-zinc-950/40 hover:bg-zinc-950">
              <Upload className="w-5 h-5 text-emerald-400" />
              <span className="text-xs font-bold text-zinc-300">
                {isUk ? "+ Завантажити вхідний лист, договір, акт, рахунок" : "+ Upload input reference files"}
              </span>
              <span className="text-[10px] text-zinc-500">
                PDF, DOCX, TXT, PNG, JPG (до 10 МБ)
              </span>
              <input
                type="file"
                multiple
                accept=".pdf,.docx,.doc,.txt,.png,.jpg,.jpeg"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>

            {attachedFiles.length > 0 && (
              <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                {attachedFiles.map((f, i) => (
                  <div key={i} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-lg px-2.5 py-1.5 text-xs">
                    <div className="flex items-center gap-2 truncate">
                      <FileText className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                      <span className="truncate text-zinc-200 text-[11px]">{f.name}</span>
                      <span className="text-[9px] text-zinc-500 font-mono">({Math.round(f.size / 1024)} KB)</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-zinc-500 hover:text-red-400 p-0.5 cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Knowledge Base Toggle */}
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-4 flex items-center justify-between gap-3 shadow-md">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 shrink-0">
                <BookOpen className="w-4 h-4" />
              </div>
              <div>
                <div className="text-xs font-bold text-white flex items-center gap-1.5">
                  <span>{isUk ? "Враховувати Базу Знань Підприємства" : "Inject Enterprise Knowledge Base"}</span>
                  <span className="px-1.5 py-0.2 rounded text-[9px] bg-amber-500/20 text-amber-300 font-mono">Auto-Sync</span>
                </div>
                <p className="text-[10px] text-zinc-400 leading-tight mt-0.5">
                  {isUk 
                    ? "Автоматично узгоджує наказ із завантаженими регламентами, інструкціями та обліковою політикою" 
                    : "Enforces compliance with internal regulations, policies, and job descriptions uploaded in KB"}
                </p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={includeKnowledgeBase}
              onChange={(e) => setIncludeKnowledgeBase(e.target.checked)}
              className="w-4 h-4 text-emerald-500 bg-zinc-950 border-zinc-700 rounded focus:ring-emerald-500 cursor-pointer"
            />
          </div>

          {/* Submit Button */}
          <button
            type="button"
            onClick={handleRunDraft}
            disabled={isDrafting}
            className="w-full py-3.5 px-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 disabled:opacity-50 text-slate-950 font-black text-xs sm:text-sm uppercase tracking-wider rounded-xl shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-2 cursor-pointer transition-all"
          >
            {isDrafting ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin text-slate-950" />
                <span>{isUk ? "Складання документа..." : "Drafting in progress..."}</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 text-slate-950" />
                <span>{isUk ? "🚀 Скласти Проєкт Документа на Бланку" : "🚀 Draft Document on Blank"}</span>
              </>
            )}
          </button>

          {error && (
            <div className="p-3 bg-red-950/40 border border-red-900/50 rounded-xl text-red-300 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Right Column: Result / Live Preview */}
        <div className="lg:col-span-7 space-y-5">
          {/* Loading Progress State */}
          {isDrafting && (
            <div className="bg-zinc-900/80 border border-emerald-500/30 rounded-2xl p-8 text-center space-y-6 shadow-2xl">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 animate-pulse">
                <FileCheck className="w-8 h-8" />
              </div>
              <div className="space-y-2">
                <h3 className="text-base font-black text-white">
                  {isUk ? "Мультиагентний синтез офіційного документа" : "Multi-Agent Synthesis in Progress"}
                </h3>
                <p className="text-xs text-emerald-400 font-mono animate-fade-in">
                  {STEPS[loadingStep]}
                </p>
              </div>

              {/* Step indicator bars */}
              <div className="grid grid-cols-5 gap-2 max-w-md mx-auto">
                {STEPS.map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-1.5 rounded-full transition-all ${
                      idx <= loadingStep ? 'bg-emerald-500' : 'bg-zinc-800'
                    }`}
                  />
                ))}
              </div>

              <div className="flex flex-wrap items-center justify-center gap-2 text-[10px] text-zinc-400 font-mono pt-2">
                <span className="px-2 py-1 bg-zinc-950 rounded border border-zinc-800">⚖️ Юрист</span>
                <span className="px-2 py-1 bg-zinc-950 rounded border border-zinc-800">💰 Бухгалтер</span>
                <span className="px-2 py-1 bg-zinc-950 rounded border border-zinc-800">👥 Кадровик</span>
                <span className="px-2 py-1 bg-zinc-950 rounded border border-zinc-800">✍️ Коректор (2019)</span>
                <span className="px-2 py-1 bg-zinc-950 rounded border border-zinc-800">🏛️ ДСТУ 4163:2020</span>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isDrafting && !result && (
            <div className="bg-zinc-900/40 border border-zinc-800/80 rounded-2xl p-10 text-center space-y-4">
              <div className="w-14 h-14 rounded-2xl bg-zinc-800/50 border border-zinc-700/50 flex items-center justify-center mx-auto text-zinc-400">
                <Building2 className="w-7 h-7" />
              </div>
              <div className="max-w-md mx-auto space-y-1.5">
                <h3 className="text-sm font-bold text-white">
                  {isUk ? "Готовність до формування офіційного документа" : "Ready to Draft Official Document"}
                </h3>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  {isUk 
                    ? "Оберіть тип документа, введіть завдання та прикріпіть файли. Агент самостійно залучить спеціалізовані промпти юриста, бухгалтера й кадровика та видасть готовий наказ або лист на фірмовому бланку."
                    : "Select document type, describe the task and attach files. The agent will coordinate specialized legal, accounting, and HR expertise to render the final document onto your letterhead."}
                </p>
              </div>
            </div>
          )}

          {/* Result View */}
          {result && (
            <div className="space-y-4 animate-fade-in">
              {/* Action Toolbar */}
              <div className="bg-zinc-900 border border-zinc-800 p-4 rounded-2xl shadow-xl flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 rounded-lg text-xs font-black bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                    {result.blankUsed.name}
                  </span>
                  <span className="text-xs text-zinc-400 font-mono">
                    {result.docNumber} • {result.docDate}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={result.docxUrl}
                    download
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-xl transition-all flex items-center gap-1.5 shadow-sm cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>DOCX (Word)</span>
                  </a>

                  {result.pdfUrl && (
                    <a
                      href={result.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white font-bold text-xs rounded-xl transition-all flex items-center gap-1.5 shadow-sm cursor-pointer"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span>PDF</span>
                    </a>
                  )}

                  <button
                    type="button"
                    onClick={() => setShowSigModal(true)}
                    className="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs rounded-xl transition-all flex items-center gap-1.5 shadow-sm cursor-pointer"
                    title={isUk ? "Підписати документ електронним підписом КЕП / Дія.Підпис" : "Sign with QES"}
                  >
                    <ShieldCheck className="w-3.5 h-3.5" />
                    <span>{isUk ? "✍️ Підписати КЕП" : "Sign QES"}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowRawMarkdown(!showRawMarkdown)}
                    className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-bold text-xs rounded-xl transition-all flex items-center gap-1.5 shadow-sm cursor-pointer border border-zinc-700"
                    title={showRawMarkdown ? (isUk ? "Повернутись до нормального форматованого вигляду" : "Switch to Normal View") : (isUk ? "Переглянути вихідну розмітку Markdown" : "View Raw Markdown")}
                  >
                    {showRawMarkdown ? (
                      <>
                        <Eye className="w-3.5 h-3.5 text-violet-400" />
                        <span>{isUk ? "👁️ Нормальний вигляд" : "Normal View"}</span>
                      </>
                    ) : (
                      <>
                        <Code className="w-3.5 h-3.5 text-zinc-400" />
                        <span>{isUk ? "Код Markdown" : "Raw Markdown"}</span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(result.rawText);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="p-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl transition-colors cursor-pointer"
                    title={isUk ? "Скопіювати текст" : "Copy text"}
                  >
                    {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Roles Used & Compliance Badge */}
              <div className="bg-zinc-950/80 border border-zinc-800 rounded-2xl p-4 space-y-2.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-zinc-300 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                    <span>{isUk ? "Залучені експертні ролі (Personal AI Vault):" : "Expert Roles Activated:"}</span>
                  </span>
                  <span className="text-emerald-400 font-mono font-bold text-[11px]">
                    ✓ 100% {result.complianceAudit.dstuStandard}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2">
                  {result.rolesUsed.map((role) => (
                    <div key={role.id} className="bg-zinc-900 border border-zinc-700/60 rounded-lg px-2.5 py-1 text-[11px]">
                      <span className="font-bold text-emerald-300">{role.name}</span>
                      <span className="text-zinc-500 ml-1.5">({role.domain})</span>
                    </div>
                  ))}
                </div>

                {result.complianceAudit.notes.length > 0 && (
                  <div className="pt-1 flex flex-wrap gap-2 text-[10px] text-zinc-400">
                    {result.complianceAudit.notes.map((note, nIdx) => (
                      <span key={nIdx} className="flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                        <span>{note}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* White Sheet / Document Preview Canvas */}
              <div className="bg-white text-zinc-900 rounded-2xl p-8 sm:p-12 shadow-2xl leading-relaxed border border-zinc-300 min-h-[500px]">
                {showRawMarkdown ? (
                  <div className="whitespace-pre-wrap font-mono text-xs bg-zinc-50 p-4 rounded-xl border border-zinc-200 text-zinc-800 select-text overflow-x-auto">
                    {result.rawText}
                  </div>
                ) : (
                  <div className="select-text">
                    <MarkdownRenderer content={result.rawText} theme="light" />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Digital Signature Modal */}
      {showSigModal && result && (
        <DigitalSignatureModal
          isOpen={showSigModal}
          onClose={() => setShowSigModal(false)}
          documentTitle={`${result.title} (${result.docNumber})`}
          documentContent={result.rawText}
          userEmail={currentUserEmail}
          onSigned={() => {
            alert(isUk ? "Документ успішно підписано КЕП та внесено до реєстру!" : "Document signed successfully!");
          }}
        />
      )}
    </div>
  );
}
